/**
 * ModernWash 일일/일별 세차 현황 — 미러(legacy_mw_*) 기반 전용 화면.
 *
 * variant='single' → 일일 세차 현황: KST 날짜 하나 선택 → 그 날 분류별 카운트 + 결제 참고치.
 * variant='range'  → 일별 세차 현황: 기간 선택 → KST 날짜별 표(실세차/미인식/중복/… + 매출 참고치).
 *
 * 데이터는 GET /owner-console/modernwash/mirror/wash-daily (라이브 V1 무접촉, 미러만).
 * 신선도 규칙 준수: never_synced/no_source_data 를 절대 "0" 으로 단정하지 않는다(renderMetricValue).
 * 실 세차는 actual_wash 만 — 다른 분류를 실세차에 합산하지 않는다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalCarWashIcon from '@mui/icons-material/LocalCarWash';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import QueryError from '../components/QueryError';
import FreshnessBanner from './modernwash/FreshnessBanner';
import { getAdminRole } from '../utils/auth';
import { statusChipSx } from '../styles/uiTokens';
import { formatWon } from '../owner-console-v2/modules/carwash/modernwash/format';
import { sectionStateMeta, renderMetricValue } from './modernwash/mirrorFormat';
import { getMirrorWashDaily } from '../api/modernwashMirror';

const SERVICE = 'modernwash';

const WASH_LABELS = {
  actual_wash: '실 세차',
  not_recognized: '미인식',
  duplicate_recognition: '중복 인식',
  audit_only: '감사 로그',
  unknown: '미분류',
};
const CLASS_ORDER = ['actual_wash', 'not_recognized', 'duplicate_recognition', 'audit_only', 'unknown'];

const surfaceSx = {
  borderRadius: '12px',
  borderColor: 'divider',
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  backgroundColor: '#fff',
};

/** now 기준 KST 날짜(YYYY-MM-DD), offsetDays 일 전. */
const kstDate = (offsetDays = 0) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(
    new Date(Date.now() - offsetDays * 86400000),
  );

const DateField = ({ label, value, max, min, onChange }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
    <Box
      component="input"
      type="date"
      aria-label={label}
      value={value}
      max={max}
      min={min}
      onChange={(e) => onChange(e.target.value)}
      sx={{
        height: 34,
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.16)',
        px: 1,
        fontSize: 13,
        fontFamily: 'inherit',
        backgroundColor: '#fff',
      }}
    />
  </Box>
);

const StatCard = ({ icon, color, label, value, sectionState, unit }) => {
  const { text, placeholder } = renderMetricValue(value, sectionState, unit);
  return (
    <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2.25 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color,
            backgroundColor: alpha(color, 0.12),
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary' }}>{label}</Typography>
      </Box>
      <Typography
        sx={{
          fontSize: placeholder ? 18 : { xs: 24, md: 28 },
          fontWeight: 800,
          lineHeight: 1.15,
          letterSpacing: '-0.02em',
          color: placeholder ? 'text.secondary' : 'text.primary',
        }}
        noWrap
      >
        {text}
      </Typography>
    </Paper>
  );
};

const ModernWashWashStatusPage = ({ variant = 'single' }) => {
  const navigate = useNavigate();
  const role = getAdminRole();
  const isMaster = role === 'master';
  const isRange = variant === 'range';
  const today = kstDate(0);

  // 날짜 상태
  const [singleDate, setSingleDate] = useState(() => kstDate(0));
  const [startDate, setStartDate] = useState(() => kstDate(6));
  const [endDate, setEndDate] = useState(() => kstDate(0));
  const start = isRange ? startDate : singleDate;
  const end = isRange ? endDate : singleDate;
  const rangeInvalid = isRange && start > end;

  // master 매장 선택
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  useEffect(() => {
    if (!isMaster) return undefined;
    let alive = true;
    api
      .get('/stores')
      .then((res) => alive && setStores(res.data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isMaster]);
  const storeParam = isMaster ? storeId || undefined : undefined;

  const [state, setState] = useState({ loading: true, error: null, res: null });
  const load = useCallback(() => {
    if (rangeInvalid) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    getMirrorWashDaily({ service: SERVICE, storeId: storeParam, period: 'custom', start, end })
      .then((res) => alive && setState({ loading: false, error: null, res }))
      .catch((e) => alive && setState({ loading: false, error: e, res: null }));
    return () => {
      alive = false;
    };
  }, [storeParam, start, end, rangeInvalid]);
  useEffect(() => load(), [load]);

  const res = state.res;
  const data = res?.data || null;
  const freshness = res?.freshness || null;
  const reason = res?.reason || null;
  const washState = data?.washDataState || 'never_synced';
  const payState = data?.paymentDataState || 'never_synced';
  const days = data?.days || [];
  const day = isRange ? null : days[0] || null; // 단일일은 최대 1개
  const totals = data?.totals || null;
  const notLinked = Boolean(reason); // 비연동 매장/사유 있음 → 본문 대신 배너의 사유만 노출

  const title = isRange ? '일별 세차 현황' : '일일 세차 현황';
  const basisLabel = isRange ? `${start} ~ ${end}` : start;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2, md: 3 } }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <IconButton size="small" onClick={() => navigate('/modernwash')} aria-label="ModernWash 운영으로">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: { xs: 20, md: 28 }, fontWeight: 800, letterSpacing: '-0.03em' }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            V1 레거시 미러 기반 · 실 세차는 actual_wash 만 집계
          </Typography>
        </Box>
        <Box sx={{ ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            size="small"
            label={isMaster ? '전체 매장 조회 (master)' : '본인 매장 고정 (manager)'}
            variant="outlined"
            sx={{ height: 30, borderRadius: 999, fontWeight: 700 }}
          />
          <IconButton size="small" onClick={load} aria-label="새로고침">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FreshnessBanner freshness={freshness} reason={reason} basisLabel={basisLabel} />
      </Box>

      {/* 날짜 컨트롤 */}
      <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 1.75, md: 2 }, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {isRange ? (
            <>
              <DateField label="시작일" value={startDate} max={endDate} onChange={setStartDate} />
              <DateField label="종료일" value={endDate} min={startDate} max={today} onChange={setEndDate} />
            </>
          ) : (
            <DateField label="조회 일자" value={singleDate} max={today} onChange={setSingleDate} />
          )}
          {isMaster ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}>매장</Typography>
              <Box
                component="select"
                aria-label="매장"
                value={storeId}
                onChange={(e) => setStoreId(e.target.value)}
                sx={{
                  height: 34,
                  borderRadius: '8px',
                  border: '1px solid rgba(0,0,0,0.16)',
                  px: 1,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  backgroundColor: '#fff',
                }}
              >
                <option value="">전체(연동 매장)</option>
                {stores.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </Box>
            </Box>
          ) : null}
        </Box>
        {rangeInvalid ? (
          <Typography sx={{ fontSize: 12.5, color: 'error.main', mt: 1 }}>
            시작일이 종료일보다 늦습니다.
          </Typography>
        ) : null}
      </Paper>

      {state.error ? (
        <QueryError error={state.error} onRetry={load} context="세차 현황" />
      ) : state.loading ? (
        <LinearProgress sx={{ mb: 2 }} />
      ) : null}

      {/* 단일일 — KPI + 분류 */}
      {!isRange && !state.error && !state.loading && !notLinked ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))' },
              gap: 2,
              mb: 3,
            }}
          >
            <StatCard
              icon={<LocalCarWashIcon />}
              color="#7C3AED"
              label="실제 세차 (actual_wash)"
              value={day?.actualWash}
              sectionState={washState}
              unit="건"
            />
            <StatCard
              icon={<PaymentIcon />}
              color="#EA580C"
              label="운영 매출 참고치"
              value={day?.grossAmount}
              sectionState={payState}
              unit="원"
            />
          </Box>

          <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 2, md: 3 } }}>
            <Typography sx={{ fontSize: { xs: 15, md: 18 }, fontWeight: 800, mb: 0.5 }}>세차 인식 분류</Typography>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
              실 세차(actual_wash)만 실제 세차입니다. 미인식·중복·감사·미분류는 별도 이벤트로 합산하지 않습니다.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' },
                gap: 1.5,
              }}
            >
              {CLASS_ORDER.map((key) => {
                const counted = key === 'actual_wash';
                const cell = renderMetricValue(day ? day.breakdown[key] : undefined, washState, '건');
                return (
                  <Box
                    key={key}
                    sx={{
                      border: '1px solid',
                      borderColor: counted ? 'rgba(124,58,237,0.35)' : 'divider',
                      borderRadius: '10px',
                      p: 1.5,
                      backgroundColor: counted ? alpha('#7C3AED', 0.05) : '#FCFCFD',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{WASH_LABELS[key]}</Typography>
                      <Box component="span" sx={statusChipSx(counted ? 'purple' : 'neutral')}>
                        {counted ? '실세차' : '실세차 아님'}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: cell.placeholder ? 14 : 22,
                        fontWeight: 800,
                        color: cell.placeholder ? 'text.secondary' : 'text.primary',
                      }}
                      noWrap
                    >
                      {cell.text}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </>
      ) : null}

      {/* 기간별 — 일자별 표 */}
      {isRange && !state.error && !state.loading && !notLinked ? (
        <Paper variant="outlined" sx={{ ...surfaceSx, p: { xs: 2, md: 3 } }}>
          <Typography sx={{ fontSize: { xs: 15, md: 18 }, fontWeight: 800, mb: 0.5 }}>일자별 세차 현황</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2 }}>
            KST 날짜 기준(최신 우선). 실 세차는 actual_wash 열만.
          </Typography>
          {washState === 'never_synced' ? (
            <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: '10px' }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>동기화 전 · 준비중</Typography>
            </Box>
          ) : days.length === 0 ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>기간 내 세차 기록이 없습니다.</Typography>
            </Box>
          ) : (
            <>
              {(washState === 'stale' || washState === 'failed_showing_last_success') && (
                <Box component="span" sx={{ ...statusChipSx(sectionStateMeta(washState).tone), mb: 1.5, display: 'inline-flex' }}>
                  {sectionStateMeta(washState).label}
                </Box>
              )}
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>날짜</TableCell>
                      {CLASS_ORDER.map((key) => (
                        <TableCell key={key} align="right">
                          {WASH_LABELS[key]}
                        </TableCell>
                      ))}
                      <TableCell align="right">매출 참고치</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {days.map((d) => (
                      <TableRow key={d.date} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 700 }}>{d.date}</TableCell>
                        {CLASS_ORDER.map((key) => (
                          <TableCell
                            key={key}
                            align="right"
                            sx={key === 'actual_wash' ? { fontWeight: 800, color: '#7C3AED' } : undefined}
                          >
                            {(d.breakdown[key] || 0).toLocaleString()}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatWon(d.grossAmount)}</TableCell>
                      </TableRow>
                    ))}
                    {totals ? (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 800 }}>합계</TableCell>
                        {CLASS_ORDER.map((key) => (
                          <TableCell
                            key={key}
                            align="right"
                            sx={{ fontWeight: 800, color: key === 'actual_wash' ? '#7C3AED' : 'text.primary' }}
                          >
                            {(totals.breakdown[key] || 0).toLocaleString()}
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                          {formatWon(totals.grossAmount)}
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      ) : null}
    </Box>
  );
};

export default ModernWashWashStatusPage;
